import { useState, useRef } from "react";
import { toast } from "sonner";
import { Download, Upload, FileDown, X, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImportExportButtons({ 
  entityName, 
  exportEndpoint, 
  importEndpoint, 
  templateEndpoint,
  onImportSuccess,
  exportFileName
}) {
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Export to Excel
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}${exportEndpoint}`);
      const data = response.data;
      
      if (!data || data.length === 0) {
        toast.error(`No ${entityName.toLowerCase()} to export`);
        return;
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, entityName);
      XLSX.writeFile(wb, `${exportFileName || entityName}_export.xlsx`);
      toast.success(`${entityName} exported successfully!`);
    } catch (error) {
      toast.error(`Failed to export ${entityName.toLowerCase()}`);
      console.error(error);
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}${templateEndpoint}`);
      const data = response.data;

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, `${exportFileName || entityName}_import_template.xlsx`);
      toast.success("Template downloaded!");
    } catch (error) {
      toast.error("Failed to download template");
      console.error(error);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        setImportData(jsonData);
        setImportResult(null);
        setShowImportModal(true);
      } catch (error) {
        toast.error("Failed to read file. Please use a valid Excel file.");
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Perform import
  const handleImport = async () => {
    if (importData.length === 0) {
      toast.error("No data to import");
      return;
    }

    setIsImporting(true);
    try {
      const response = await axios.post(`${API}${importEndpoint}`, importData);
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} ${entityName.toLowerCase()}`);
        if (onImportSuccess) {
          onImportSuccess();
        }
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} error(s) occurred during import`);
      }
    } catch (error) {
      toast.error("Failed to import data");
      console.error(error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
          title={`Export ${entityName}`}
          data-testid={`export-${entityName.toLowerCase()}-btn`}
        >
          <Download className="w-4 h-4" />
          Export
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
          title={`Import ${entityName}`}
          data-testid={`import-${entityName.toLowerCase()}-btn`}
        >
          <Upload className="w-4 h-4" />
          Import
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".xlsx,.xls,.csv"
          className="hidden"
        />
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-2xl border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Import {entityName}</h3>
              <button
                onClick={() => { setShowImportModal(false); setImportData([]); setImportResult(null); }}
                className="p-2 text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Download Template Button */}
            <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">Need a template?</p>
                  <p className="text-white/60 text-sm">Download the import template to see the required format</p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="download-template-btn"
                >
                  <FileDown className="w-4 h-4" />
                  Download Template
                </button>
              </div>
            </div>

            {/* Preview Data */}
            {importData.length > 0 && !importResult && (
              <div className="mb-4">
                <p className="text-white mb-2">Preview ({importData.length} rows):</p>
                <div className="bg-white/5 rounded-lg overflow-x-auto max-h-60">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        {Object.keys(importData[0]).map((key) => (
                          <th key={key} className="text-left p-2 text-white/60">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importData.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="border-b border-white/5">
                          {Object.values(row).map((val, i) => (
                            <td key={i} className="p-2 text-white/80">{String(val).substring(0, 30)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importData.length > 5 && (
                    <p className="p-2 text-white/40 text-center">... and {importData.length - 5} more rows</p>
                  )}
                </div>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <div className="mb-4 space-y-3">
                <div className="flex items-center gap-2 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400">Successfully imported: {importResult.imported}</span>
                </div>
                
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-red-400 mb-2">
                      <AlertCircle className="w-5 h-5" />
                      <span>Errors ({importResult.errors.length}):</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {importResult.errors.map((err, idx) => (
                        <p key={idx} className="text-red-300 text-sm">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowImportModal(false); setImportData([]); setImportResult(null); }}
                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                {importResult ? "Close" : "Cancel"}
              </button>
              {!importResult && (
                <button
                  onClick={handleImport}
                  disabled={isImporting || importData.length === 0}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-import-btn"
                >
                  {isImporting ? "Importing..." : `Import ${importData.length} rows`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
